import React, { Component, Fragment } from "react";
import Moment from "moment";
import "moment/locale/es";
import Select from "react-select";
import { CSVLink } from "react-csv";
import Breadcrumb from "../common/breadcrumb";
import AuthService from "../services/auth.service";
import ApiService from "../services/api.service";
import { SortableTh, sortRows, nextSort } from "../common/sortable-th";

Moment.locale("es");

// mismo criterio que "Desempeño de SKUs": todo el histórico se calcula/trae
// igual, pero en pantalla se muestra de a ventanas de 12 meses hacia atrás
// desde el mes elegido -- si no, son 40+ columnas de una sola vez
const NUM_MONTHS_WINDOW = 12;

function fmtMoney(val) {
  if (val === null || val === undefined || val === "") return "—";
  const num = parseFloat(val);
  if (isNaN(num)) return "—";
  return (
    "S/ " +
    num.toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function fmtGrams(val) {
  if (val === null || val === undefined || val === "") return "—";
  const num = parseFloat(val);
  if (isNaN(num)) return "—";
  return (
    num.toLocaleString("es-PE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }) + " g"
  );
}

// las claves de mes vienen como "YYYY-M" (o "YYYY-MM"); se ordenan
// cronológicamente y se formatean lindo para el header de la columna
function monthKeyLabel(key) {
  const m = Moment(key, "YYYY-M");
  return m.isValid() ? m.format("MMM YYYY") : key;
}

// métricas del select "Métrica": cada celda (cliente x mes) muestra el
// monto total comprado, el ticket promedio (total / nº de compras) o el
// peso acumulado (Σ cantidad × peso de productos/presentaciones de
// categorías con "Analítica de pesos").
// Un mes sin compras queda en 0 en todas
const METRIC_OPTIONS = [
  { value: "total", label: "Total de compras" },
  { value: "ticket", label: "Ticket promedio" },
  { value: "grams", label: "Peso acumulado" },
];

const METRIC_TITLES = {
  total: "Ventas por cliente y mes",
  ticket: "Ticket promedio por cliente y mes",
  grams: "Peso acumulado por cliente y mes",
};

const CSV_PREFIXES = {
  total: "cohortes-de-clientes",
  ticket: "ticket-promedio-cohortes",
  grams: "peso-acumulado-cohortes",
};

function csvFilename(metric) {
  const prefix = CSV_PREFIXES[metric] || CSV_PREFIXES.total;
  return `${prefix}_${Moment().format("YYYY-MM-DD")}.csv`;
}

// filtro de texto sobre todas las columnas de la fila
function filterRows(rows, filter) {
  const val = filter.toLowerCase();
  if (!val || rows.length === 0) return rows;
  const columns = Object.keys(rows[0]);
  return rows.filter((d) => {
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      if (d[col] && d[col].toString().toLowerCase().indexOf(val) > -1)
        return true;
    }
    return false;
  });
}

export class VerReportCustomerCohorts extends Component {
  constructor(props) {
    super(props);
    this.onChangeFilter = this.onChangeFilter.bind(this);
    this.handleSort = this.handleSort.bind(this);

    this.handleChange = this.handleChange.bind(this);
    this.handleMetricChange = this.handleMetricChange.bind(this);

    this.state = {
      results: [],
      temp: [],
      monthKeys: [],
      filter: "",
      loading: false,
      page: 1,
      // orden por defecto: cohorte más antiguo primero (lectura natural
      // de una tabla de cohortes, de arriba hacia abajo)
      sort: { key: "CohorteKey", dir: "asc" },
      dataCateg: [],
      dataListCateg: [],
      selectedOption: null,
      metric: METRIC_OPTIONS[0],
      // filas ya armadas por métrica ({ total: [...], ticket: [...], grams: [...] }) --
      // cambiar de métrica no vuelve a pedir nada al backend
      rowsByMetric: { total: [], ticket: [], grams: [] },
    };
  }

  // arranca con el menú lateral cerrado para dejar más espacio a la tabla
  // (mismo patrón que ver-report-order-list / ver-customer-profile)
  componentDidMount() {
    document.querySelector(".page-main-header") &&
      document.querySelector(".page-main-header").classList.add("open");
    document.querySelector(".page-sidebar") &&
      document.querySelector(".page-sidebar").classList.add("open");
    this.getData();
    this.getDataCateg();
  }

  // selector de mes, igual que "Desempeño de SKUs" -- acá no cambia qué se
  // pide al backend (siempre se trae todo el histórico), solo qué ventana
  // de columnas se muestra (ver getVisibleMonthKeys)
  getDataCateg() {
    const user = AuthService.getCurrentUser();
    ApiService.getDataReportsMonthsAndYearsSku(user.token).then((res) => {
      if (res && res.status === "success") {
        const items = res.data.map((row, i) => ({
          value: i,
          label: Moment(`${row.year2}-${row.month2}`, "YYYY-M").format(
            "MMMM YYYY",
          ),
        }));
        this.setState({ dataCateg: items, dataListCateg: res.data });

        const now = Moment();
        const currentIndex = res.data.findIndex(
          (row) =>
            String(row.year2) === now.format("YYYY") &&
            String(row.month2) === now.format("M"),
        );
        if (currentIndex >= 0) {
          this.setState({ selectedOption: items[currentIndex] });
        }
      }
    });
  }

  handleChange(selectedOption) {
    this.setState({ selectedOption, page: 1 });
  }

  handleMetricChange(metric) {
    const { rowsByMetric, filter } = this.state;
    const rows = rowsByMetric[metric.value];
    this.setState({
      metric,
      temp: rows,
      results: filterRows(rows, filter),
      page: 1,
    });
  }

  // ventana de NUM_MONTHS_WINDOW meses hacia atrás desde el mes elegido,
  // acotada a los meses que realmente existen en los datos
  getVisibleMonthKeys() {
    const { monthKeys, selectedOption, dataListCateg } = this.state;
    if (!selectedOption) return monthKeys;

    const entry = dataListCateg[selectedOption.value];
    const anchor = Moment(`${entry.year2}-${entry.month2}`, "YYYY-M");
    const windowStart = anchor
      .clone()
      .subtract(NUM_MONTHS_WINDOW - 1, "months");

    return monthKeys.filter((key) => {
      const m = Moment(key, "YYYY-M");
      return (
        m.isSameOrAfter(windowStart, "month") &&
        m.isSameOrBefore(anchor, "month")
      );
    });
  }

  // solo clientes con algo de actividad dentro de la ventana visible --
  // mismo criterio que SKU (no muestra productos/clientes en 0 en todas
  // las columnas visibles)
  getVisibleResults(visibleMonthKeys) {
    const { results, selectedOption } = this.state;
    if (!selectedOption) return results;
    return results.filter((row) =>
      visibleMonthKeys.some((key) => (parseFloat(row[key]) || 0) > 0),
    );
  }

  componentWillUnmount() {
    document.querySelector(".page-main-header") &&
      document.querySelector(".page-main-header").classList.remove("open");
    document.querySelector(".page-sidebar") &&
      document.querySelector(".page-sidebar").classList.remove("open");
  }

  getData() {
    const user = AuthService.getCurrentUser();
    this.setState({ loading: true });

    ApiService.getCustomerCohorts(user.token).then(
      (res) => {
        if (res && res.status === "success") {
          const combinedReports = res.data.combinedReports;
          const monthKeys = [...new Set(res.data.headersTable)].sort((a, b) => {
            const ma = Moment(a, "YYYY-M");
            const mb = Moment(b, "YYYY-M");
            return ma.valueOf() - mb.valueOf();
          });

          // 1 fila = 1 cliente (igual que "Desempeño de SKUs" con
          // 1 fila = 1 producto). Cohorte queda como columna
          // informativa para poder ordenar/agrupar por mes de
          // entrada; los meses antes de su 1ra compra quedan en 0.
          const rowsTotal = [];
          const rowsTicket = [];
          const rowsGrams = [];
          for (const idUser in combinedReports) {
            if (!combinedReports.hasOwnProperty(idUser)) continue;
            const dataRows = combinedReports[idUser];
            const first = dataRows[0];
            const cohortKey = `${first.cohort_year}-${first.cohort_month}`;

            const row = {
              IdUser: idUser,
              Cliente: first.name,
              CohorteKey: cohortKey,
              Cohorte: monthKeyLabel(cohortKey),
            };
            monthKeys.forEach((key) => {
              row[key] = 0;
            });
            const rowTicket = { ...row };
            const rowGrams = { ...row };
            dataRows.forEach((data) => {
              const dateKey = `${data.report_year}-${data.report_month}`;
              if (row.hasOwnProperty(dateKey)) {
                const total = parseFloat(data.total) || 0;
                const numPurchases = parseInt(data.num_purchases, 10) || 0;
                row[dateKey] = total;
                rowTicket[dateKey] =
                  numPurchases > 0
                    ? Math.round((total / numPurchases) * 100) / 100
                    : 0;
                rowGrams[dateKey] = parseFloat(data.total_grams) || 0;
              }
            });

            rowsTotal.push(row);
            rowsTicket.push(rowTicket);
            rowsGrams.push(rowGrams);
          }

          const rowsByMetric = {
            total: rowsTotal,
            ticket: rowsTicket,
            grams: rowsGrams,
          };
          const rows = rowsByMetric[this.state.metric.value];

          this.setState({
            rowsByMetric,
            results: filterRows(rows, this.state.filter),
            temp: rows,
            monthKeys,
            loading: false,
            page: 1,
          });
        } else {
          this.setState({
            rowsByMetric: { total: [], ticket: [], grams: [] },
            results: [],
            temp: [],
            monthKeys: [],
            loading: false,
          });
        }
      },
      () => {
        this.setState({ loading: false });
      },
    );
  }

  onChangeFilter(e) {
    const filter = e.target.value;
    this.setState({
      filter,
      results: filterRows(this.state.temp, filter),
      page: 1,
    });

    e.preventDefault();
  }

  handleSort(key) {
    this.setState((prev) => ({
      sort: nextSort(prev.sort, key),
      page: 1,
    }));
  }

  renderPagination(total, page, pageSize, onPageChange) {
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return null;
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderTop: "1px solid #f0f0f0",
          gap: 8,
          background: "#fafafa",
          fontSize: 13,
          color: "#666",
        }}
      >
        <span style={{ fontSize: 12, color: "#aaa" }}>
          {from}–{to} de {total}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
            style={{
              border: "1px solid #ddd",
              borderRadius: 6,
              padding: "4px 12px",
              background: page === 1 ? "#f4f4f4" : "#fff",
              color: page === 1 ? "#ccc" : "#333",
              cursor: page === 1 ? "default" : "pointer",
              fontSize: 13,
            }}
          >
            <i className='fa fa-chevron-left' />
          </button>
          <span style={{ padding: "4px 10px", fontSize: 13, color: "#555" }}>
            {page} / {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
            style={{
              border: "1px solid #ddd",
              borderRadius: 6,
              padding: "4px 12px",
              background: page === totalPages ? "#f4f4f4" : "#fff",
              color: page === totalPages ? "#ccc" : "#333",
              cursor: page === totalPages ? "default" : "pointer",
              fontSize: 13,
            }}
          >
            <i className='fa fa-chevron-right' />
          </button>
        </div>
      </div>
    );
  }

  renderResults() {
    const { results, page, sort, loading } = this.state;
    if (loading)
      return <div style={{ padding: 16, color: "#888" }}>Cargando...</div>;
    if (results.length === 0) {
      return (
        <div style={{ padding: 16, color: "#888" }}>
          No hay clientes con compras todavía.
        </div>
      );
    }

    const visibleMonthKeys = this.getVisibleMonthKeys();
    const visibleResults = this.getVisibleResults(visibleMonthKeys);
    if (visibleResults.length === 0) {
      return (
        <div style={{ padding: 16, color: "#888" }}>
          Ningún cliente tuvo compras en la ventana de meses elegida.
        </div>
      );
    }

    const PAGE_SIZE = 100;
    const sorted = sortRows(visibleResults, sort);
    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const paginated = sorted.slice(
      (currentPage - 1) * PAGE_SIZE,
      currentPage * PAGE_SIZE,
    );

    const headers = [
      { label: "Cliente", key: "Cliente", align: "left" },
      { label: "Cohorte", key: "CohorteKey", align: "left" },
      ...visibleMonthKeys.map((key) => ({
        label: monthKeyLabel(key),
        key,
        align: "right",
      })),
    ];

    return (
      <div
        style={{
          border: "1px solid #ebebeb",
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8f9fa",
                  borderBottom: "2px solid #ebebeb",
                }}
              >
                {headers.map((h) => (
                  <SortableTh
                    key={h.key}
                    header={h}
                    sort={sort}
                    onSort={this.handleSort}
                    style={{ textAlign: h.align }}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.map((item) => (
                <tr
                  key={item.IdUser}
                  style={{ borderBottom: "1px solid #f4f4f4" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f8fbff")
                  }
                  onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                >
                  <td
                    style={{
                      padding: "9px 12px",
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                      textAlign: "left",
                    }}
                  >
                    {item.Cliente}
                  </td>
                  <td
                    style={{
                      padding: "9px 12px",
                      whiteSpace: "nowrap",
                      textAlign: "left",
                      textTransform: "capitalize",
                    }}
                  >
                    <span
                      style={{
                        background: "#d4edda",
                        color: "#155724",
                        borderRadius: 10,
                        padding: "2px 9px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {item.Cohorte}
                    </span>
                  </td>
                  {visibleMonthKeys.map((key) => (
                    <td
                      key={key}
                      style={{
                        padding: "9px 12px",
                        whiteSpace: "nowrap",
                        color: "#666",
                        textAlign: "right",
                      }}
                    >
                      {this.state.metric.value === "grams"
                        ? fmtGrams(item[key])
                        : fmtMoney(item[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {this.renderPagination(sorted.length, currentPage, PAGE_SIZE, (p) =>
          this.setState({ page: p }),
        )}
      </div>
    );
  }

  render() {
    const { sort, selectedOption, metric } = this.state;
    const visibleMonthKeys = this.getVisibleMonthKeys();
    const visibleResults = this.getVisibleResults(visibleMonthKeys);
    const sortedResults = sortRows(visibleResults, sort);
    const csvHeaders = [
      { label: "Cliente", key: "Cliente" },
      { label: "Cohorte", key: "Cohorte" },
      ...visibleMonthKeys.map((key) => ({ label: monthKeyLabel(key), key })),
    ];

    return (
      <Fragment>
        <Breadcrumb title='Cohortes de Clientes' parent='Analítica' />

        <div className='container-fluid'>
          <div className='row'>
            <div className='col-sm-12'>
              <div className='card' style={{ marginBottom: 16 }}>
                <div className='card-body' style={{ padding: "12px 16px" }}>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#555",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Mes
                    </span>
                    <div
                      style={{
                        flex: "1 1 200px",
                        minWidth: 160,
                        maxWidth: 280,
                      }}
                    >
                      <Select
                        placeholder='Elegí un mes...'
                        value={selectedOption}
                        onChange={this.handleChange}
                        options={this.state.dataCateg}
                        isClearable
                      />
                    </div>
                    <span style={{ fontSize: 12, color: "#999" }}>
                      (ventana de {NUM_MONTHS_WINDOW} meses hacia atrás desde el
                      mes elegido)
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#555",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Métrica
                    </span>
                    <div
                      style={{
                        flex: "1 1 200px",
                        minWidth: 160,
                        maxWidth: 240,
                      }}
                    >
                      <Select
                        value={metric}
                        onChange={this.handleMetricChange}
                        options={METRIC_OPTIONS}
                        isSearchable={false}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                className='card'
                style={{ boxShadow: "none", border: "1px solid #ebebeb" }}
              >
                <div
                  className='card-header'
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                  }}
                >
                  <h5 style={{ margin: 0 }}>{METRIC_TITLES[metric.value]}</h5>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <input
                      placeholder='Buscar...'
                      name='filter'
                      type='text'
                      className='form-control'
                      autoComplete='off'
                      onInput={this.onChangeFilter}
                      style={{
                        flex: "1 1 180px",
                        minWidth: 140,
                        maxWidth: 260,
                        height: 32,
                        boxSizing: "border-box",
                        padding: "4px 10px",
                      }}
                    />
                    {sortedResults.length > 0 && (
                      <CSVLink
                        className='btn btn-outline-primary btn-sm'
                        data={sortedResults}
                        headers={csvHeaders}
                        filename={csvFilename(metric.value)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          height: 32,
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                        }}
                      >
                        <i
                          className='fa fa-file-excel-o'
                          style={{ marginRight: 6 }}
                        />
                        Exportar
                      </CSVLink>
                    )}
                  </div>
                </div>
                <div className='card-body' style={{ padding: 0 }}>
                  {this.renderResults()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

export default VerReportCustomerCohorts;
